import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from "react";
import { useUPSApiController } from "@app/ups/hooks";
import {
  CalculateInstallmentRequestData,
  CalculateInstallmentResponseData,
  ConfirmPaymentInput,
  ConfirmPaymentPlanInput,
  ConfirmPaymentPlanResponseData,
  CreatePaymentMethodInput,
  CreatePaymentMethodResponseData,
  CreatePaymentPlanInput,
  GuestValidationInputData,
  GuestValidationResponseData,
  LoginValidationInputData,
  LoginValidationResponseData,
  PaymentPlanRequestResponseData,
  PaymentRequestResponseData,
  ResponseData,
  UpdatePaymentPlanInput,
} from "../models";
import { UPSContext } from "./upsProvider";
import { getTokenExpireTimeFromJwt } from "@app/postBill/utils";
import { useUPSSessionProvider } from "@app/ups/provider";
import { logMsg } from "@app/utils/logMsg";

type UPSAPIProviderState = {
  loadingStates: Record<string, boolean>;
  guestValidationData?: GuestValidationResponseData;
  paymentRequestData?: PaymentRequestResponseData;
  paymentSetupData?: CreatePaymentMethodResponseData;
  paymentPlanRequestData?: PaymentPlanRequestResponseData;
};

export interface UPSAPIController {
  upsApiProviderState: UPSAPIProviderState;
  UPS_BASE_API_URL: string;
  loadingStates: Record<string, boolean>;
  guestValidation: (
    input: GuestValidationInputData
  ) => Promise<ResponseData<GuestValidationResponseData>>;
  loginValidation: (
    input: LoginValidationInputData
  ) => Promise<ResponseData<LoginValidationResponseData>>;
  createPaymentMethod: (
    input: CreatePaymentMethodInput
  ) => Promise<ResponseData<CreatePaymentMethodResponseData>>;
  updatePaymentMethod: (input: UpdatePaymentPlanInput) => Promise<ResponseData>;
  getPaymentRequest: (
    paymentRequestId: string
  ) => Promise<ResponseData<PaymentRequestResponseData>>;
  createDirectPayment: (
    paymentRequestId: string
  ) => Promise<ResponseData<CreatePaymentMethodResponseData>>;
  createPaymentPlan: (
    paymentRequestId: string,
    input: CreatePaymentPlanInput
  ) => Promise<ResponseData<CreatePaymentMethodResponseData>>;
  confirmDirectPayment: (
    paymentRequestId: string,
    input: ConfirmPaymentInput
  ) => Promise<ResponseData>;
  confirmPaymentPlan: (
    paymentRequestId: string,
    input: ConfirmPaymentPlanInput
  ) => Promise<ResponseData<ConfirmPaymentPlanResponseData>>;
  getPaymentPlanRequest: (
    paymentPlanId: string
  ) => Promise<ResponseData<PaymentPlanRequestResponseData>>;
  calculateInstallment: (
    input: CalculateInstallmentRequestData
  ) => Promise<ResponseData<CalculateInstallmentResponseData[]>>;
  getReceiptRequest: (receiptId: string) => Promise<ArrayBuffer>;
}
export const Context = createContext<UPSAPIController>({
  upsApiProviderState: {
    loadingStates: {},
  },
  UPS_BASE_API_URL: "",
  guestValidation: async () => Promise.reject(),
  loginValidation: async () => Promise.reject(),
  createPaymentMethod: async () => Promise.reject(),
  updatePaymentMethod: async () => Promise.reject(),
  getPaymentRequest: async () => Promise.reject(),
  createDirectPayment: async () => Promise.reject(),
  createPaymentPlan: async () => Promise.reject(),
  confirmDirectPayment: async () => Promise.reject(),
  confirmPaymentPlan: async () => Promise.reject(),
  getPaymentPlanRequest: async () => Promise.reject(),
  calculateInstallment: async () => Promise.reject(),
  getReceiptRequest: async () => Promise.reject(),
  loadingStates: {},
});

Context.displayName = "UPSAPIContext";

interface UPSAPIProviderProps {
  children: ReactNode;
}

const UPSAPIProvider: FC<UPSAPIProviderProps> = ({ children }) => {
  const [upsApiProviderState, setUpsApiProviderState] =
    useState<UPSAPIProviderState>({
      loadingStates: { createPaymentMethod: false, updatePaymentMethod: false },
    });
  const { setUPSPatientUid, setUPSAccessToken } = useContext(UPSContext);
  const { setExpireTime } = useUPSSessionProvider();

  const logException = (error: unknown, context?: string): void => {
    logMsg("error", "ups.api", `${context ?? "request"} failed`, {
      errorType: error instanceof Error ? error.name : "UnknownError",
    });
  };

  const setLoadingState = (key: string, value: boolean) => {
    setUpsApiProviderState((prevState) => ({
      ...prevState,
      loadingStates: {
        ...prevState.loadingStates,
        [key]: value,
      },
    }));
  };

  const setTokenAndGetExpireTime = (
    responseData: GuestValidationResponseData
  ) => {
    const token = responseData?.access_token;
    setUPSAccessToken(token as string);
    const tokenExpireTimestamp = getTokenExpireTimeFromJwt(token)?.exp;
    const expiresAt = tokenExpireTimestamp * 1000;
    setExpireTime(expiresAt);
  };

  const {
    UPS_BASE_API_URL,
    updatePaymentMethod,
    createPaymentMethod,
    getPaymentRequest,
    createDirectPayment,
    createPaymentPlan,
    confirmDirectPayment,
    confirmPaymentPlan,
    guestValidation,
    loginValidation,
    getPaymentPlanRequest,
    calculateInstallment,
    getReceiptRequest,
  } = useUPSApiController();

  const handleGuestValidation = async (
    data: GuestValidationInputData
  ): Promise<ResponseData<GuestValidationResponseData>> => {
    try {
      setLoadingState("guestValidation", true);
      const response = await guestValidation(data);
      setUpsApiProviderState((prevState) => ({
        ...prevState,
        guestValidationData: response?.data,
      }));
      setTokenAndGetExpireTime(response?.data as GuestValidationResponseData);
      return response;
    } catch (error) {
      logException(error, "guestValidation");
      throw error;
    } finally {
      setLoadingState("guestValidation", false);
    }
  };

  const handleLoginValidation = async (
    data: LoginValidationInputData
  ): Promise<ResponseData<LoginValidationResponseData>> => {
    try {
      const response = await loginValidation(data);
      setUpsApiProviderState((prevState) => ({
        ...prevState,
        guestValidationData: response?.data,
      }));
      setUPSAccessToken(response?.data?.access_token as string);
      return response;
    } catch (error) {
      logException(error, "loginValidation");
      throw error;
    }
  };

  const handleCreatePaymentMethod = async (
    data: CreatePaymentMethodInput
  ): Promise<ResponseData<CreatePaymentMethodResponseData>> => {
    try {
      setLoadingState("createPaymentMethod", true);
      const response = await createPaymentMethod(data);
      return response;
    } catch (error) {
      logException(error, "createPaymentMethod");
      throw error;
    } finally {
      setLoadingState("createPaymentMethod", false);
    }
  };

  const handleUpdatePaymentMethod = async (
    data: UpdatePaymentPlanInput
  ): Promise<ResponseData> => {
    try {
      setLoadingState("updatePaymentMethod", true);
      const response = await updatePaymentMethod(data);
      return response;
    } catch (error) {
      logException(error, "updatePaymentMethod");
      throw error;
    } finally {
      setLoadingState("updatePaymentMethod", false);
    }
  };

  const handleGetPaymentRequest = async (
    paymentRequestId: string
  ): Promise<ResponseData<PaymentRequestResponseData>> => {
    try {
      setLoadingState("getPaymentRequest", true);
      const response = await getPaymentRequest(paymentRequestId);
      setUpsApiProviderState((prevState) => ({
        ...prevState,
        paymentRequestData: response?.data,
      }));
      setUPSPatientUid(response?.data?.patientUid as string);
      return response;
    } catch (error) {
      logException(error, "getPaymentRequest");
      throw error;
    } finally {
      setLoadingState("getPaymentRequest", false);
    }
  };

  const handleCreateDirectPayment = async (
    paymentRequestId: string
  ): Promise<ResponseData<CreatePaymentMethodResponseData>> => {
    try {
      setLoadingState("createDirectPayment", true);
      const response = await createDirectPayment(paymentRequestId);
      setUpsApiProviderState((prevState) => ({
        ...prevState,
        paymentSetupData: response?.data,
      }));
      return response;
    } catch (error) {
      logException(error, "createDirectPayment");
      throw error;
    } finally {
      setLoadingState("createDirectPayment", false);
    }
  };

  const handleCreatePaymentPlan = async (
    paymentRequestId: string,
    data: CreatePaymentPlanInput
  ): Promise<ResponseData<CreatePaymentMethodResponseData>> => {
    try {
      setLoadingState("createDirectPayment", true);
      const response = await createPaymentPlan(paymentRequestId, data);
      setUpsApiProviderState((prevState) => ({
        ...prevState,
        paymentSetupData: response?.data,
      }));
      return response;
    } catch (error) {
      logException(error, "createPaymentPlan");
      throw error;
    } finally {
      setLoadingState("createDirectPayment", false);
    }
  };

  const handleConfirmDirectPayment = async (
    paymentRequestId: string,
    data: ConfirmPaymentInput
  ): Promise<ResponseData> => {
    try {
      setLoadingState("confirmPayment", true);
      const response = await confirmDirectPayment(paymentRequestId, data);
      return response;
    } catch (error) {
      logException(error, "confirmDirectPayment");
      throw error;
    } finally {
      setLoadingState("confirmPayment", false);
    }
  };

  const handleConfirmPaymentPlan = async (
    paymentRequestId: string,
    data: ConfirmPaymentPlanInput
  ): Promise<ResponseData<ConfirmPaymentPlanResponseData>> => {
    try {
      setLoadingState("confirmPayment", true);
      const response = await confirmPaymentPlan(paymentRequestId, data);
      return response;
    } catch (error) {
      logException(error, "confirmPaymentPlan");
      throw error;
    } finally {
      setLoadingState("confirmPayment", false);
    }
  };

  const handleGetPaymentPlanRequest = async (
    paymentPlanId: string
  ): Promise<ResponseData<PaymentPlanRequestResponseData>> => {
    try {
      setLoadingState("getPaymentPlanRequest", true);
      const response = await getPaymentPlanRequest(paymentPlanId);
      setUpsApiProviderState((prevState) => ({
        ...prevState,
        paymentPlanRequestData: response?.data,
      }));
      setUPSPatientUid(response?.data?.patientUid as string);
      return response;
    } catch (error) {
      logException(error, "getPaymentPlanRequest");
      throw error;
    } finally {
      setLoadingState("getPaymentPlanRequest", false);
    }
  };

  const handleCalculateInstallment = async (
    input: CalculateInstallmentRequestData
  ): Promise<ResponseData<CalculateInstallmentResponseData[]>> => {
    try {
      setLoadingState("getPaymentRequest", true);
      const response = await calculateInstallment(input);
      setUpsApiProviderState((prevState) => ({
        ...prevState,
      }));
      return response;
    } catch (error) {
      logException(error, "calculateInstallment");
      throw error;
    } finally {
      setLoadingState("getPaymentRequest", false);
    }
  };

  const handleGetreceiptRequest = async (
    receiptId: string
  ): Promise<ArrayBuffer> => {
    try {
      setLoadingState("getReceiptRequest", true);
      return await getReceiptRequest(receiptId);
    } catch (error) {
      logException(error, "getReceiptRequest");
      throw error;
    } finally {
      setLoadingState("getReceiptRequest", false);
    }
  };

  const value = {
    upsApiProviderState,
    UPS_BASE_API_URL,
    guestValidation: handleGuestValidation,
    loginValidation: handleLoginValidation,
    createPaymentMethod: handleCreatePaymentMethod,
    updatePaymentMethod: handleUpdatePaymentMethod,
    getPaymentRequest: handleGetPaymentRequest,
    createDirectPayment: handleCreateDirectPayment,
    createPaymentPlan: handleCreatePaymentPlan,
    confirmDirectPayment: handleConfirmDirectPayment,
    confirmPaymentPlan: handleConfirmPaymentPlan,
    getPaymentPlanRequest: handleGetPaymentPlanRequest,
    calculateInstallment: handleCalculateInstallment,
    getReceiptRequest: handleGetreceiptRequest,
    loadingStates: upsApiProviderState.loadingStates ?? {},
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default UPSAPIProvider;
